<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { PositionsDataPayload } from "@/types/interface/position";
import { usePositionStore } from "@/stores/master/position";

const idPosition = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const positionStore = usePositionStore();
const detailPosition = ref<any>();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    code: zod.string().nonempty({ message: "Code is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: code } = useField<string>("code");
const { value: name } = useField<string>("name");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadPosition: PositionsDataPayload = {
    code: code.value,
    name: name.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await positionStore.dispatchUpdate(
      idPosition.value,
      payloadPosition
    );
  } else {
    responseApi.value = await positionStore.dispatchCreate(payloadPosition);
  }
  const { success, message } = responseApi.value;

  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Position Successfully Updated"
          : "Position Successfully Added"
      );
      router.push("/master/position");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailPosition = async () => {
  const { success, message } = await positionStore.dispatchDetail(
    idPosition.value
  );
  if (success) {
    detailPosition.value = await positionStore.position.data;
    name.value = detailPosition.value.name;
    code.value = detailPosition.value.code;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idPosition.value = router.currentRoute.value.params.id.toString();
    loadDetailPosition();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Position</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/position"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Position
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="code" class="form-label label text-secondary"
                    >Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="code"
                      v-model="code"
                      placeholder="Position Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.code }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
